import React from 'react'
import { ElementType, forwardRef, HTMLAttributes, ReactNode, useContext } from 'react'
import classNames from 'classnames'
import { ModalContext } from './Modal'
import { CloseButton } from '../CloseButton'
import { Text } from '../Text'

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
    /** Rendered before the title, e.g. a DocumentIcon */
    icon?: ReactNode
    children?: ReactNode
    className?: string
    title?: ReactNode
    subtitle?: string
    tag?: ElementType
    /** Add a close button to the header. */
    closeButton?: boolean
    /** Truncate the title to a single line with an ellipsis. */
    truncateTitle?: boolean
}

const ModalHeader = forwardRef<HTMLDivElement, Props>(({
    icon,
    title,
    subtitle,
    tag = 'h3',
    children,
    className,
    closeButton = true,
    truncateTitle = false,
    ...otherProps
}, ref) => {

    const { handleClose } = useContext(ModalContext)
    const _className = classNames('modal-header', className)

    const closeClicked = () => {
        handleClose()
    }

    return (
        <>
            <div
                ref={ref}
                className={_className}
                {...otherProps}
            >
                <div className={classNames('modal-header-content d-flex align-items-center gap-2', {
                    'flex-grow-1 overflow-hidden min-width-0': truncateTitle
                })}>
                    {icon && (
                        <span className="modal-header-icon">
                            {icon}
                        </span>
                    )}
                    {title && (
                        <Text
                            tag={tag}
                            heavy
                            title={(truncateTitle && typeof title === 'string') ? title : undefined}
                            className={classNames('modal-title', { 'text-truncate min-width-0': truncateTitle })}>
                            {title}
                        </Text>
                    )}
                    {children}
                </div>
                {(closeButton) && (
                    <CloseButton
                        onClick={() => closeClicked()}
                    />
                )}
            </div>
            {subtitle && (
                <div className="modal-subtitle" >
                    <Text tag="h5" className="modal-subtitle pb-2" >
                        {subtitle || ''}
                    </Text>
                </div>
            )}
        </>
    )
})

export default ModalHeader
ModalHeader.displayName = 'ModalHeader'
